import { useSelector } from 'react-redux';
import { matchPath, Navigate, Outlet, useLocation } from 'react-router-dom';
import { examsSelectors } from '../slices/exams/examsSlice';
import type { UserExam } from '../types/User';
import { isNclexExam, isVideosOffForExam } from '../utils/examUtils';

// Case-insensitive like the router itself (`matchPath` defaults to
// caseSensitive: false), so /Videos cannot slip past the guard.
const isUnderPath = (pathname: string, base: string) =>
  Boolean(matchPath(`${base}/*`, pathname));

/**
 * True when the primary exam has no such section (ORA-952 single-exam rule):
 * an exam with videos off (NCLEX-RN) has no /videos, and the games (Step-only
 * Doctordle, DDx, Daily Question) are not offered to an NCLEX primary.
 * Medical exams and a visitor with no primary exam are never redirected.
 */
export const isRouteOffForPrimaryExam = (
  pathname: string,
  primaryExam?: UserExam | null
) => {
  if (isUnderPath(pathname, '/videos')) return isVideosOffForExam(primaryExam);
  if (isUnderPath(pathname, '/games')) return isNclexExam(primaryExam);
  return false;
};

/**
 * Pathless layout route around the /videos and /games subtrees. A hidden
 * section is never mounted: the redirect replaces the page element, so the
 * page's own mount effects (fetches, search-param writes) cannot run or
 * cancel the navigation home.
 */
const ExamFeatureRouteGuard = () => {
  const { pathname } = useLocation();
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);

  if (isRouteOffForPrimaryExam(pathname, primaryExam)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

export default ExamFeatureRouteGuard;
